/**************
 Compare Component
 ***************/

// Compare State Singleton
class CompareState {
    static instance = null;
    static MAX_ITEMS = 3;
    static STORAGE_KEY = 'compareProducts';

    constructor() {
        if (CompareState.instance) {
            return CompareState.instance;
        }
        CompareState.instance = this;
        this.handles = this.loadFromStorage();
        this.productCache = {};
        this.init();
    }

    init() {
        this.createStickyBar();
        this.updateStickyBar();
    }

    loadFromStorage() {
        try {
            const stored = localStorage.getItem(CompareState.STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    }

    saveToStorage() {
        localStorage.setItem(CompareState.STORAGE_KEY, JSON.stringify(this.handles));
    }

    isSelected(handle) {
        return this.handles.includes(handle);
    }

    async add(handle) {
        if (this.handles.length >= CompareState.MAX_ITEMS) {
            return false;
        }
        if (!this.handles.includes(handle)) {
            this.handles.push(handle);
            this.saveToStorage();
            await this.fetchProductData(handle);
            this.dispatchUpdate();
        }
        return true;
    }

    remove(handle) {
        const index = this.handles.indexOf(handle);
        if (index > -1) {
            this.handles.splice(index, 1);
            this.saveToStorage();
            this.dispatchUpdate();
        }
    }

    toggle(handle) {
        if (this.isSelected(handle)) {
            this.remove(handle);
            return false;
        } else {
            return this.add(handle);
        }
    }

    clear() {
        this.handles = [];
        this.saveToStorage();
        this.dispatchUpdate();
    }

    async fetchProductData(handle) {
        if (this.productCache[handle]) {
            return this.productCache[handle];
        }
        try {
            const response = await fetch(`/products/${handle}?view=json`);
            const data = await response.json();
            this.productCache[handle] = data;
            return data;
        } catch (e) {
            console.error('Failed to fetch product data:', e);
            return null;
        }
    }

    async getProductData(handle) {
        if (this.productCache[handle]) {
            return this.productCache[handle];
        }
        return await this.fetchProductData(handle);
    }

    dispatchUpdate() {
        window.dispatchEvent(new CustomEvent('compare-updated', {
            detail: {handles: this.handles}
        }));
        this.updateStickyBar();
    }

    createStickyBar() {
        // Don't render the bar on the compare page
        if (window.location.pathname.includes('/pages/compare-products')) return;

        if (document.querySelector('.compareBar')) return;

        const bar = document.createElement('div');
        bar.className = 'compareBar hidden';
        bar.innerHTML = `
            <div class="compareBarInner">
                <div class="compareBarItems"></div>
                <div class="compareBarActions">
                    <span class="compareBarCount">0 of ${CompareState.MAX_ITEMS} selected</span>
                    <a href="/pages/compare-products" class="compareBarButton button">Compare Now</a>
                </div>
            </div>
        `;
        document.body.appendChild(bar);
    }

    async updateStickyBar() {
        const bar = document.querySelector('.compareBar');
        if (!bar) return;

        const itemsContainer = bar.querySelector('.compareBarItems');
        const countEl = bar.querySelector('.compareBarCount');
        const compareBtn = bar.querySelector('.compareBarButton');

        // Update visibility
        if (this.handles.length === 0) {
            bar.classList.add('hidden');
            return;
        } else {
            bar.classList.remove('hidden');
        }

        // Update count
        countEl.textContent = `${this.handles.length} of ${CompareState.MAX_ITEMS} selected`;


        // Fetch all product data
        const products = await Promise.all(
            this.handles.map(handle => this.getProductData(handle))
        );

        // Build items HTML
        let itemsHTML = '';
        products.forEach((product, i) => {
            if (!product) return;
            const handle = this.handles[i];
            itemsHTML += `
                <div class="compareBarItem" data-handle="${handle}">
                    <button class="compareBarItemRemove" data-handle="${handle}" aria-label="Remove ${product.title}">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                    <img class="compareBarItemImage" src="${product.featured_image}" alt="${product.title}">
                    <span class="compareBarItemTitle">${product.title}</span>
                </div>
            `;
        });
        itemsContainer.innerHTML = itemsHTML;

        // Add remove event listeners
        itemsContainer.querySelectorAll('.compareBarItemRemove').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const handle = btn.dataset.handle;
                this.remove(handle);
            });
        });
    }
}

// Initialize singleton
const compareState = new CompareState();

// Compare Button Custom Element
class CompareButton extends HTMLElement {
    constructor() {
        super();
        this.handle = null;
    }

    connectedCallback() {
        this.handle = this.dataset.handle;
        if (!this.handle) return;

        this.render();
        this.setupListeners();
    }

    render() {
        const isSelected = compareState.isSelected(this.handle);
        this.innerHTML = `
            <button class="compareButton ${isSelected ? 'active' : ''}" aria-pressed="${isSelected}">
                <svg class="compareButtonIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path fill="currentColor" d="M384 96L512 96C529.7 96 544 110.3 544 128C544 145.7 529.7 160 512 160L398.4 160C393.2 185.8 375.5 207.1 352 217.3L352 512L512 512C529.7 512 544 526.3 544 544C544 561.7 529.7 576 512 576L128 576C110.3 576 96 561.7 96 544C96 526.3 110.3 512 128 512L288 512L288 217.3C264.5 207 246.8 185.7 241.6 160L128 160C110.3 160 96 145.7 96 128C96 110.3 110.3 96 128 96L256 96C270.6 76.6 293.8 64 320 64C346.2 64 369.4 76.6 384 96zM439.6 384L584.4 384L512 259.8L439.6 384zM512 480C449.1 480 396.8 446 386 401.1C383.4 390.1 387 378.8 392.7 369L487.9 205.8C492.9 197.2 502.1 192 512 192C521.9 192 531.1 197.3 536.1 205.8L631.3 369C637 378.8 640.6 390.1 638 401.1C627.2 445.9 574.9 480 512 480zM126.8 259.8L54.4 384L199.3 384L126.8 259.8zM.9 401.1C-1.7 390.1 1.9 378.8 7.6 369L102.8 205.8C107.8 197.2 117 192 126.9 192C136.8 192 146 197.3 151 205.8L246.2 369C251.9 378.8 255.5 390.1 252.9 401.1C242.1 445.9 189.8 480 126.9 480C64 480 11.7 446 .9 401.1z"/></svg>
                <span class="compareButtonText">${isSelected ? 'Added' : 'Compare'}</span>
                ${isSelected ? `
                    <svg class="compareButtonCheck" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                ` : ''}
            </button>
        `;
    }

    setupListeners() {
        this.querySelector('.compareButton').addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isSelected = compareState.isSelected(this.handle);

            if (isSelected) {
                compareState.remove(this.handle);
            } else {
                const added = await compareState.add(this.handle);
                if (!added) {
                    // Max items reached
                    if (typeof Swal !== 'undefined') {
                        Swal.fire({
                            icon: 'info',
                            title: 'Maximum reached',
                            text: `You can compare up to ${CompareState.MAX_ITEMS} products at a time.`,
                            timer: 3000,
                            showConfirmButton: false
                        });
                    }
                }
            }
        });

        window.addEventListener('compare-updated', () => {
            this.render();
            this.setupListeners();
        });
    }
}

// Register custom element
if (!customElements.get('compare-button')) {
    customElements.define('compare-button', CompareButton);
}
